import { Injectable } from '@angular/core';
import { BackendService } from '@tg4/http-infrastructure/dist/src';
import { Observable } from 'rxjs';
import { Evaluation360Record } from './_interfaces/360-evaluation-record.interface';
import { Evaluated360User } from './_interfaces/360-evaluation-user.interface';
import { AreaConfigAttributeAction } from './_interfaces/area-config.interface';

@Injectable()
export class Evaluation360Service {

    constructor(private _httpService: BackendService) {
    }

    public getUser360Evaluators(cycleId: number): Observable<any> {
        return this._httpService.get('getUser360Evaluators', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public getUsersFor360Evaluation(
        cycleId: number,
        searchString: string = '',
        userId?: string,
        attributeType: number = null,
        attributeAction = AreaConfigAttributeAction.Include,
        areaId?: number,
    ): Observable<any> {
        const parameters = [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'searchString', 'value': searchString },
            { 'name': 'attributeAction', 'value': attributeAction },
            { 'name': 'areaId', 'value': areaId },
        ];
        if (userId)
            parameters.push({ 'name': 'userId', 'value': userId });

        if (attributeType)
            parameters.push({ 'name': 'attributeType', 'value': attributeType });

        return this._httpService.get('getUsersFor360Evaluation', [], parameters);
    }

    public saveUsersFor360Evaluation(
        cycleParticipantId: number, cycleId: number, selectedUsers: Array<Evaluated360User>
    ): Observable<any> {
        return this._httpService.post('saveUsersFor360Evaluation', {
            'cycleId': cycleId,
            'cycleParticipantId': cycleParticipantId,
            'selectedEvaluators': selectedUsers.map(u => {
                return {
                    'areaId': u.areaId,
                    'cycleParticipantId': u.cycleParticipantId
                };
            })
        });
    }

    public sendLastDayEmail(cycleId: number, forceSend: boolean, validate: boolean = false): Observable<any> {
        return this._httpService.post('360EvaluationSendLastDay', {
            'cycleId': cycleId,
            'forceSend': forceSend,
            'validate': validate
        });
    }

    public reopenEvaluation(cycleId: number, SubjectUserId: string): Observable<any> {
        return this._httpService.post('reopen360Evaluation', {
            'cycleId': cycleId,
            'subjectUserId': SubjectUserId
        });
    }

    public deleteEvaluation(cycleId: number, SubjectUserId: string): Observable<any> {
        return this._httpService.delete('delete360Evaluation', [
            { 'name': 'CycleId', 'value': cycleId as any }, { 'name': 'SubjectUserId', 'value': SubjectUserId as any }]);
    }

    public deleteEvaluations(cycleId: number, SubjectUserIds: string[]): Observable<any> {
        return this._httpService.delete('refuse360Evaluations', [
            { 'name': 'CycleId', 'value': cycleId as any }, { 'name': 'SubjectUserIds', 'value': SubjectUserIds as any }]);
    }

    public reopenValidation(cycleId: number, SubjectUserId: string): Observable<any> {
        return this._httpService.post('reopenSubordinateUsersFor360Validation', {
            'cycleId': cycleId,
            'subjectUserId': SubjectUserId
        });
    }

    public getUsersFor360Validation(cycleId: number): Observable<any> {
        return this._httpService.get('getUsersFor360Validation', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public getSubordinateUsersFor360Validation(
        cycleId: number, subordinateId: string
    ): Observable<any> {
        return this._httpService.get('getSubordinateUsersFor360Validation', [], [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'subordinateId', 'value': subordinateId }
        ]);
    }

    public manageSelectedUsersFor360Evaluation(
        cycleParticipantId: number, evaluators: Array<Evaluated360User>
    ): Observable<any> {
        return this._httpService.put('manageSelectedUsersFor360Evaluation', {
            'cycleParticipantId': cycleParticipantId,
            'selectedEvaluators': evaluators
        });
    }

    public changeRelationShipOn360Evaluation(
        cycleParticipantId: number, subjectUserId: string, relation: string, cycleId: number
    ): Observable<any> {
        return this._httpService.put('changeRelationShipOn360Evaluation', {
            'cycleParticipantId': cycleParticipantId,
            'subjectUserId': subjectUserId,
            'relation': relation,
            'cycleId': cycleId
        });
    }

    public getEvalsToDelete(
        cycleParticipantId: number, cycleEvaluatorId: number): Observable<any> {
        return this._httpService.get('getEvalsToDelete', [], [
            { 'name': 'cycleParticipantId', 'value': cycleParticipantId as any },
            { 'name': 'cycleEvaluatorId', 'value': cycleEvaluatorId as any }
        ]);
    }

    public removeSelection(cycleParticipantId: number, subjectUserId: string): Observable<any> {
        return this._httpService.delete('removeSelection', [
            { name: 'CycleParticipantId', value: cycleParticipantId as any },
            { name: 'SubjectUserId', value: subjectUserId as any }
        ]);
    }

    public getApprovedUsersFor360Evaluation(
        cycleId: number
    ): Observable<any> {
        return this._httpService.get('getApprovedUsersFor360Evaluation', [], [
            { 'name': 'cycleId', 'value': cycleId as any }
        ]);
    }

    public getApprovedUserFor360Evaluation(
        cycleId: number, userId: string
    ): Observable<any> {
        return this._httpService.get('getApprovedUserFor360Evaluation', [], [
            { 'name': 'cycleId', 'value': cycleId as any },
            { 'name': 'userId', 'value': userId }
        ]);
    }

    public save360Evaluation(evaluation: Evaluation360Record, isManager: boolean = false): Observable<any> {
        return isManager ?
            this._httpService.post('save360ManagerEvaluation', evaluation) :
            this._httpService.post('save360Evaluation', evaluation);
    }

    public send360Evaluation(evaluation: Evaluation360Record, isManager: boolean = false): Observable<any> {
        return isManager ?
            this._httpService.post('send360ManagerEvaluation', evaluation) :
            this._httpService.post('send360Evaluation', evaluation);
        return this._httpService.post('', evaluation);
    }

}
